import { useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';

import type { NarrativeForm } from '../../utils/schema';
import { htmlToText } from '@/components/RichTextEditor';
import {
  narrativeGetValuatorQueryParamInitialValues,
  transformFormValuesToValuation,
} from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import useUser from '@/hooks/useUser';
import NarrativeOnboardingModal from '@/components/NarrativeOnboardingModal';
import merge from 'lodash/merge';
import { useNarrativeInvalidate } from '../../hooks/useNarrativeInvalidate';
import { usePublishNarrativeUpdateWithSnackbar } from '../../hooks/usePublishNarrative/usePublishNarrative';
import {
  getInitialUpdateId,
  getInitialContent,
  getInitialTitle,
  getIsPublishedLocked,
  getIsPublishedWithinGracePeriod,
} from './utils';
import { useUpdateNarrativeUpdateWithSnackbar } from '../../hooks/useUpdateNarrativeUpdate/useUpdateNarrativeUpdate';
import { useNarrativeFormManager } from '../../hooks/useNarrativeFormManager';
import { hasErrors } from '../../hooks/useNarrativeForm';
import { useNarrativeValuationManager } from '../../hooks/useNarrativeValuationManager';
import { NarrativeFormContent } from '../NarrativeFormContent';
import { useHasSavedContent } from '../../hooks/useHasSavedContent';
import type { MyNarrativeCompany } from '../../hooks/useNarrativeCompany';

type Props = {
  myNarrativeCompany?: MyNarrativeCompany['Company'];
};

export const PublicNarrativeEdit = ({ myNarrativeCompany }: Props) => {
  const myNarrative = myNarrativeCompany?.myNarrative;

  const [narrativeId, setNarrativeId] = useState<string>(myNarrative?.id ?? '');

  const company = myNarrativeCompany;

  if (!company) throw new Error("Narrative's company is missing");

  const owner = myNarrative?.owner;

  const isPublishedWithinGracePeriod =
    getIsPublishedWithinGracePeriod(myNarrative);

  const isPublishedLocked =
    getIsPublishedLocked(myNarrative) && myNarrative?.draftUpdate !== null;

  const userHoldsPosition = isPublishedWithinGracePeriod
    ? myNarrative?.latestPublishedUpdate?.userHoldsPosition
    : myNarrative?.draftUpdate?.userHoldsPosition;

  const submitLabel = isPublishedWithinGracePeriod ? 'Save' : 'Publish';

  const { data: user } = useUser();

  const [showingOnboarding, setShowingOnboarding] = useState(
    !Boolean(user.displayName)
  );

  const {
    valuation,
    calculateValuationInitialisation,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
    calculateFairValue,
    pastAnnualRevenueEarnings,
  } = useNarrativeValuationManager({
    draftUpdate: myNarrative?.draftUpdate,
    latestPublishedUpdate: myNarrative?.latestPublishedUpdate,
    company,
  });

  const { hasSavedContent, setHasSavedContent } = useHasSavedContent();

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    companyId: company.id,
  });

  const { goBack } = useNarrativeBack();
  const { invalidate } = useNarrativeInvalidate();

  useEffect(() => {
    if (!showingOnboarding && !user?.displayName) {
      goBack();
    }
  }, [showingOnboarding, user?.displayName, goBack]);

  const goBackWithSuccess = () => {
    invalidate();

    trackEvent({
      action: isPublishedWithinGracePeriod ? 'save' : 'publish',
    });

    goBack();
  };

  const [successSnackbar, setSuccessSnackbar] = useState(false);

  const {
    mutate: publishNarrativeUpdateAsync,
    status: publishNarrativeUpdateStatus,
  } = usePublishNarrativeUpdateWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const initialUpdateId = getInitialUpdateId(
    myNarrative?.latestPublishedUpdate ?? null,
    myNarrative?.draftUpdate ?? null
  );

  const initialContent = getInitialContent(
    myNarrative?.draftUpdate ?? null,
    myNarrative?.latestPublishedUpdate ?? null
  );

  const initialContentText = htmlToText(initialContent);

  const initialTitle = getInitialTitle(
    myNarrative?.draftUpdate ?? null,
    myNarrative?.latestPublishedUpdate ?? null
  );

  const [narrativeUpdateId, setNarrativeUpdateId] = useState<string | null>(
    initialUpdateId
  );

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = valuation
      ? calculateValuationInitialisation({
          valuation,
          calculateFairValueFromInitialisation,
          calculateRevenueGrowthRateFromFairValue,
        })
      : calculateFairValueFromInitialisation();

    return merge(
      {
        ...calculatorOutputs,
        userHoldsPosition: userHoldsPosition ?? false,
        title: initialTitle ?? '',
        content: {
          text: initialContentText ?? '',
          html: initialContent ?? '',
        },
        coreContent: {
          text: initialContentText ?? '',
          html: initialContent ?? '',
        },
      },
      narrativeGetValuatorQueryParamInitialValues()
    );
  }, [
    calculateValuationInitialisation,
    valuation,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    userHoldsPosition,
    initialTitle,
    initialContentText,
    initialContent,
  ]);

  const {
    values,
    errors,
    setSubmitCount,
    validateForm,
    resetForm,
    handleContentChange,
    handleTitleChange,
    handleSetFairValue,
    handleSetPartialValues,
    updateNarrativeUpdateStatus,
    startNarrativeStatus,
  } = useNarrativeFormManager({
    initialNarrativeForm,
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    trackEvent,
    narrativeUpdateId,
    narrativeId,
    companyId: company.id,
    successSnackbar,
    setNarrativeUpdateId,
    disableAutoSave: isPublishedWithinGracePeriod,
    setNarrativeId,
  });

  useEffect(() => {
    if (updateNarrativeUpdateStatus === 'success') {
      setHasSavedContent(true);
    }
  }, [updateNarrativeUpdateStatus, setHasSavedContent]);

  useEffect(() => {
    if (startNarrativeStatus === 'success') {
      setHasSavedContent(true);
    }
  }, [startNarrativeStatus, setHasSavedContent]);

  const {
    mutate: updateNarrativeUpdateAsync,
    status: updateNarrativeUpdateStatusLocal,
  } = useUpdateNarrativeUpdateWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const handleSubmit = async () => {
    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    if (!narrativeUpdateId) {
      throw new Error(
        `Narrative update ID is missing, narrativeId: ${narrativeId}, start typing to create a new narrative`
      );
    }

    const userHoldsPosition = values.userHoldsPosition ?? false;

    await updateNarrativeUpdateAsync({
      id: narrativeUpdateId,
      input: {
        contentHtml: values.content.html,
        coreHtml: values.content.html,
        title: values.title,
        setAsPrimary: true,
        valuation: {
          ...transformFormValuesToValuation({ values }),
        },
        userHoldsPosition,
      },
    });

    if (
      myNarrative?.draftUpdate !== null &&
      !myNarrative?.draftUpdate.publishedAt
    ) {
      await publishNarrativeUpdateAsync({
        id: narrativeUpdateId,
        input: { userHoldsPosition },
      });
    }

    setSubmitCount(0);
    goBackWithSuccess();
  };

  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={company.id}
          isOpen={showingOnboarding}
          onClose={() => setShowingOnboarding(false)}
        />
      </Track>
    );

  const isLoading =
    publishNarrativeUpdateStatus === 'pending' ||
    updateNarrativeUpdateStatusLocal === 'pending';

  const cancelLabel =
    isPublishedLocked || isPublishedWithinGracePeriod ? 'Close' : 'Close draft';

  return (
    <Track>
      <NarrativeFormContent
        initialFairValue={initialNarrativeForm.fairValue}
        errors={errors}
        values={values}
        onChange={handleSetPartialValues}
        onSetFairValue={handleSetFairValue}
        onContentChange={handleContentChange}
        onHeaderChange={handleTitleChange}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel={submitLabel}
        company={company}
        initialContentHtml={initialContent}
        owner={owner}
        onReset={resetForm}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        calculationBreakDownOutput={calculateBreakDown({
          discountRatePercentage: values.discountRatePercentage,
          futurePE: values.priceEarningsMultiple,
          profitMarginPercentage: values.profitMarginPercentage,
          revenueGrowthRatePercentage: values.revenuePercentage,
          timeFrame: values.timePeriod,
        })}
        hasSavedContent={hasSavedContent}
        setSuccessSnackbar={setSuccessSnackbar}
        isPublishedLocked={isPublishedLocked}
        cancelLabel={cancelLabel}
      />
    </Track>
  );
};
